import { Mid, SpotAssetContext } from "@decibeltrade/sdk";
import { QueryFunctionContext, useQuery, useSuspenseQuery } from "@tanstack/react-query";

import { readDexSdk } from "../dex-sdk";
import { useSpotMidsSubscription } from "../subscriptions/spot-mids";

/** Keyed by spot market address. */
export type SpotAssetContextsByAddr = Record<string, SpotAssetContext>;

export const SPOT_ASSET_CONTEXTS_QUERY_KEY = ["spotAssetContexts"] as const;

export function spotAssetContextsQuery() {
  return {
    queryKey: SPOT_ASSET_CONTEXTS_QUERY_KEY,
    // Merged here, not in structuralSharing: that also runs on setQueryData and would undo WS frames.
    queryFn: async ({ client }: QueryFunctionContext) => {
      const contexts = await readDexSdk.spotAssetContexts.getAll();
      const incoming = contexts.reduce<SpotAssetContextsByAddr>((acc, context) => {
        acc[context.market_addr] = context;
        return acc;
      }, {});
      return keepLivePrices(
        client.getQueryData<SpotAssetContextsByAddr>(SPOT_ASSET_CONTEXTS_QUERY_KEY),
        incoming,
      );
    },
    // WS frames never carry prev_day_price; polling keeps the 24h baseline rolling.
    refetchInterval: 1000 * 30,
  };
}

/**
 * The WS owns mid/last price once a market is cached; a poll, served from a
 * server cache up to 10s old, would otherwise roll them back.
 */
function keepLivePrices(
  previous: SpotAssetContextsByAddr | undefined,
  incoming: SpotAssetContextsByAddr,
): SpotAssetContextsByAddr {
  if (!previous) return incoming;
  const merged: SpotAssetContextsByAddr = {};
  for (const [addr, context] of Object.entries(incoming)) {
    const live = previous[addr];
    merged[addr] = live
      ? {
          ...context,
          mid: live.mid,
          last_price: live.last_price,
          timestamp_unix_ms: live.timestamp_unix_ms,
        }
      : context;
  }
  return merged;
}

export function useSpotAssetContexts() {
  useSpotMidsSubscription();
  return useSuspenseQuery(spotAssetContextsQuery());
}

export function useSpotAssetContextsQuery() {
  useSpotMidsSubscription();
  return useQuery(spotAssetContextsQuery());
}

/** Display price for a spot market: live mid, else last trade. */
export function spotDisplayPrice(context: SpotAssetContext | undefined): number | undefined {
  return context?.mid ?? context?.last_price ?? undefined;
}

/**
 * 24h change in percent units (5 = +5%), matching the perp asset-context
 * `price_change_pct_24h` convention. Undefined when the market has no trade
 * or no 24h-ago baseline (render as n/a).
 */
export function spotChangePct24h(context: SpotAssetContext | undefined): number | undefined {
  if (context?.last_price == null || !context.prev_day_price) {
    return undefined;
  }
  return ((context.last_price - context.prev_day_price) / context.prev_day_price) * 100;
}

/**
 * Merge an `all_spot_mids` WS frame into the cached contexts. A null mid is
 * meaningful (one-sided book) and overwrites; a null last trade just means
 * "no fill in this frame" and keeps the previous value.
 */
export function applySpotMids(
  contexts: SpotAssetContextsByAddr,
  mids: Mid[],
): SpotAssetContextsByAddr {
  let next: SpotAssetContextsByAddr | undefined;
  for (const mid of mids) {
    if (mid.asset_type !== "spot") continue;
    const existing = contexts[mid.market_addr];
    if (!existing) continue;
    next ??= { ...contexts };
    next[mid.market_addr] = {
      ...existing,
      mid: mid.mid,
      last_price: mid.last_trade_price ?? existing.last_price,
      timestamp_unix_ms: mid.transaction_unix_ms,
    };
  }
  return next ?? contexts;
}
